import { ColumnReader } from './metabaseTable';

/**
 * The five confidence columns a metric's own Metabase question returns alongside the standard row.
 *
 * The schema is per-metric and NOT universal, on either layer:
 *
 *   - Customer 360: 15 of the 95 metrics declare them (captured on `api-uat.perxtech.io`,
 *     2026-09-04: C-BFSI-001, C-COM-003..007, C-COM-015, C-COM-016, C-COM-019, C-COM-020, C-HYB-002,
 *     C-HYB-003, C-HYB-005, C-HYB-006, C-HYB-008). Contract PLA-8825.
 *   - Customer Insights (Layer 1): 19 of the 65 identifiers the registry reads declare them
 *     (UAT tenant `generic`, 2026-09-08, `start_date=2026-06-15&end_date=2026-07-31`: O-BFSI-001,
 *     O-BFSI-002, O-BFSI-004, O-COM-007..010, O-COM-020, O-COM-021, O-COM-023..026, O-HYB-001,
 *     O-HYB-002, O-HYB-004, O-HYB-006, O-HYB-007, O-HYB-013). The other 46 declare none —
 *     O-REV-001/002/003, O-COM-013, O-COM-019 and O-COM-032 among them. PLA-8815.
 *
 * Named here rather than inferred so a column the schema does not carry is `null` and never a
 * substituted default.
 */
export const CONFIDENCE_COLUMNS = [
  'confidence_score',
  'confidence_band',
  'confidence_limiting_reason',
  'confidence_cap_applied',
  'handoff_confidence_ok'
] as const;

/**
 * One reading's confidence, as the wire stated it.
 *
 * `band` is kept as the server's own STRING rather than narrowed to a union: `Calculation &
 * Governance Rules` G32 requires the FE to render the server-provided card score and band and
 * forbids recalculating either, so a band word this file does not recognise must still survive the
 * boundary intact. `displayConfidenceBand` below is the only thing that buckets it, and only for a
 * consumer that needs one of four treatments.
 */
export interface MetricConfidence {
  /**
   * A 0.00–1.00 decimal, per the catalogue (O-COM-023 reads `0.9129` on UAT `generic`, 2026-09-08).
   * `null` where the column answered nothing.
   */
  score: number | null;
  /** The server's band word, verbatim — `high`, `low` and `insufficient` all observed on UAT. */
  band: string | null;
  /** A governed reason code, not display copy — the same treatment `reason_code` gets. */
  limitingReason: string | null;
  capApplied: boolean | null;
  handoffOk: boolean | null;
}

export type ConfidenceBand = 'high' | 'medium' | 'low' | 'insufficient';

/**
 * The catalogue's band floors, highest first. A DISPLAY FALLBACK and nothing else.
 *
 * PLA-8825 `Calculation & Governance Rules` G32: *"FE must render the server-provided card
 * score/band and must not independently recalculate it."* So this maps a score the server already
 * published onto one of four treatments when — and only when — the server sent no band word with
 * it. It is NOT a scoring model: the catalogue's 25/20/20/15/20 per-component weighting is
 * deliberately absent from this repo, and deriving a METRIC score here rather than reading one would
 * be the exact thing G32 forbids.
 *
 * Shared by both layers: Customer 360 (`C-*`) and Customer Insights (`O-*`) bucket against these
 * same floors, so a customer's band cannot depend on which screen is asking. PLA-8815, PLA-8828.
 */
export const CONFIDENCE_BAND_FLOORS: ReadonlyArray<readonly [ConfidenceBand, number]> = [
  ['high', 0.8],
  ['medium', 0.6],
  ['low', 0.4]
];

/** The four band words, matched after case and separators are stripped. */
const BAND_WORDS: Record<string, ConfidenceBand> = {
  high: 'high',
  medium: 'medium',
  low: 'low',
  insufficient: 'insufficient',
  insufficientdata: 'insufficient'
};

/**
 * The catalogue's "Handoff minimum 0.60" — the floor a reading must clear to be handed to the
 * Growth Planner. Numerically equal to the `medium` floor and kept separate anyway: they are two
 * governed decisions, and a later change to one must not silently move the other.
 *
 * The wire states the verdict itself in `handoff_confidence_ok` (`false` on O-BFSI-001's 0.5879,
 * `true` on O-COM-023's 0.9129, UAT `generic` 2026-09-08), so this exists to LABEL that boolean,
 * never to re-derive it.
 */
export const HANDOFF_CONFIDENCE_MINIMUM = 0.6;

/**
 * A published score bucketed by `CONFIDENCE_BAND_FLOORS`. `null` for anything that is not a finite
 * number — an absent score has no band, and `insufficient` is a real verdict about a real score
 * rather than the word for "we were told nothing".
 */
export const confidenceBandFor = (score: number | null | undefined): ConfidenceBand | null => {
  if (typeof score !== 'number' || !Number.isFinite(score)) {
    return null;
  }

  const floor = CONFIDENCE_BAND_FLOORS.find(([, at]) => score >= at);

  return floor ? floor[0] : 'insufficient';
};

/**
 * The server's band word as one of the four treatments, or `null` when it is a word this file does
 * not know. `null` rather than a guess: a consumer that cannot bucket an unrecognised band should
 * print `confidence.band` verbatim, which is what G32 asks for anyway.
 */
export const normaliseConfidenceBand = (band: string | null | undefined): ConfidenceBand | null => {
  if (typeof band !== 'string') {
    return null;
  }

  return BAND_WORDS[band.toLowerCase().replace(/[^a-z]/g, '')] ?? null;
};

/**
 * Which of the four treatments to draw, or `null` for none.
 *
 * The server's band always wins, and a band word this file does not recognise wins TOO -- it
 * returns `null` rather than falling through to the score. Bucketing an unrecognised band by its
 * score is precisely the independent recalculation G32 forbids: a server that said `Provisional`
 * would be drawn as `High`. The score is consulted ONLY where no band word came with it at all.
 */
export const displayConfidenceBand = (
  confidence: MetricConfidence | null | undefined
): ConfidenceBand | null => {
  if (!confidence) {
    return null;
  }

  if (typeof confidence.band === 'string' && confidence.band.trim() !== '') {
    return normaliseConfidenceBand(confidence.band);
  }

  return confidenceBandFor(confidence.score);
};

/**
 * A row's confidence, or `undefined` where this metric's question declares none of the five columns.
 *
 * `undefined` and not an all-null object, and the distinction is load-bearing: 80 of the 95
 * Customer 360 metrics and 46 of the 65 Layer 1 identifiers have no confidence schema at all, and an
 * object of nulls would read as "confidence was asked for and withheld" on every one of them. Within
 * a metric that DOES carry the schema, each field is `null` where its own column answered nothing.
 *
 * A `ColumnReader` is taken rather than a row plus a column list because `metricResult.ts` resolves
 * every column index ONCE per envelope and this is called once per row — see `rowsFromEnvelope`.
 */
export const confidenceFromRow = (
  read: ColumnReader,
  row: unknown[]
): MetricConfidence | undefined => {
  if (!CONFIDENCE_COLUMNS.some(name => read.has(name))) {
    return undefined;
  }

  return {
    score: read.number(row, 'confidence_score'),
    band: read.text(row, 'confidence_band'),
    limitingReason: read.text(row, 'confidence_limiting_reason'),
    capApplied: read.boolean(row, 'confidence_cap_applied'),
    handoffOk: read.boolean(row, 'handoff_confidence_ok')
  };
};
